import { collection, addDoc, updateDoc, deleteDoc, doc, getDoc, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { workoutTemplateToFirestore } from "../models/workout/workoutConverter";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";

/**
 * WorkoutTemplateService
 * CRUD de modelos de treino reutilizáveis.
 * Coleção: tenants/{idTenant}/workoutTemplates
 */
const COLLECTION_NAME = "workoutTemplates";

const WorkoutTemplateService = {
    /**
     * Busca todos os modelos do tenant.
     */
    getAll: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTION_NAME),
                orderBy("createdAt", "desc")
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (error) {
            console.error("Erro ao listar modelos de treino:", error);
            throw error;
        }
    },

    /**
     * Busca modelos filtrados por tipo de treino.
     */
    getByType: async (idTenant, workoutType) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTION_NAME),
                where("workoutType", "==", workoutType),
                orderBy("createdAt", "desc")
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (error) {
            console.error("Erro ao listar modelos por tipo:", error);
            throw error;
        }
    },

    /**
     * Busca um modelo pelo ID.
     */
    getById: async (idTenant, templateId) => {
        if (!idTenant || !templateId) throw new Error("Tenant e Template IDs são obrigatórios.");

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTION_NAME, templateId);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) return null;
            return { id: docSnap.id, ...docSnap.data() };
        } catch (error) {
            console.error("Erro ao buscar modelo:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza um modelo de treino.
     * Usa workoutTemplateToFirestore para garantir padrão de dados.
     */
    save: async (templateData, idTenant, authUser) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const parsedData = workoutTemplateToFirestore(templateData, { id: idTenant }, authUser);

        try {
            if (templateData.id) {
                // UPDATE
                const docRef = doc(db, "tenants", idTenant, COLLECTION_NAME, templateData.id);
                await updateDoc(docRef, parsedData);

                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.WORKOUTS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: templateData.id,
                    entityName: templateData.title || "Modelo de treino",
                    description: `Atualizou modelo de treino "${templateData.title}"`
                });

                return { id: templateData.id, ...parsedData };
            } else {
                // CREATE
                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTION_NAME), parsedData);

                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.WORKOUTS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: templateData.title || "Modelo de treino",
                    description: `Criou modelo de treino "${templateData.title}"`
                });

                return { id: newDocRef.id, ...parsedData };
            }
        } catch (error) {
            console.error("Erro no WorkoutTemplateService.save:", error);
            throw error;
        }
    },

    /**
     * Remove um modelo de treino.
     */
    delete: async (templateId, idTenant, authUser, templateTitle) => {
        if (!idTenant || !templateId) throw new Error("Tenant e Template IDs são obrigatórios.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTION_NAME, templateId));

            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.WORKOUTS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: templateId,
                entityName: templateTitle || "Modelo de treino",
                description: `Excluiu modelo de treino "${templateTitle || templateId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no WorkoutTemplateService.delete:", error);
            throw error;
        }
    }
};

export default WorkoutTemplateService;
